/**
 * The block under the selected tab of a two-tab switcher (Gabriel 2026-10-05, admin chat rooms;
 * the client chat's Secure and History tabs share it, Gabriel 2026-10-06). Its two edges move
 * apart: the leading edge runs ahead on a stiff spring that never overshoots and stops exactly at
 * the far side, the trailing edge follows on a softer one and closes the stretch. A switch
 * mid-travel starts both edges from where they are. At rest it sits on percentage insets, so a
 * resize keeps it in place, and it has no transform (WKWebView).
 *
 * The track is the indicator's parent: two equal grid columns `gap` px apart inside `pad` px of
 * padding. The look (fill, radius, top and bottom) comes from `className`.
 */

import { useEffect, useRef, useState } from 'react';
import { useAnimate, useReducedMotion } from 'framer-motion';

const EDGE_LEAD = { type: 'spring', stiffness: 600, damping: 50 } as const;
const EDGE_TRAIL = { type: 'spring', stiffness: 260, damping: 34 } as const;

interface SlidingTabIndicatorProps {
  /** 0 for the first tab, 1 for the second. */
  index: number;
  className: string;
  /** The track's padding in px. */
  pad: number;
  /** The gap between the two columns in px. */
  gap: number;
}

export function SlidingTabIndicator({ index, className, pad, gap }: SlidingTabIndicatorProps) {
  const reduced = useReducedMotion();
  const [scope, animate] = useAnimate<HTMLSpanElement>();
  const shown = useRef(index);
  const insets = (at: number) => (at === 0
    ? { left: `${pad}px`, right: `calc(50% + ${gap / 2}px)` }
    : { left: `calc(50% + ${gap / 2}px)`, right: `${pad}px` });
  // Rendered once: a re-render must never move the block, or the first switch starts at its end.
  const [restInsets] = useState(() => insets(index));
  useEffect(() => {
    if (shown.current === index) return;
    shown.current = index;
    const el = scope.current;
    const track = el?.parentElement;
    if (!el || !track || reduced) {
      if (el) Object.assign(el.style, insets(index));
      return;
    }
    // Edges in px from the track's sides.
    const column = (track.clientWidth - 2 * pad - gap) / 2;
    const far = pad + column + gap;
    const target = index === 0 ? { left: pad, right: far } : { left: far, right: pad };
    // Start from the rendered px (the rest insets are calc() strings, or an edge is mid-flight).
    const now = getComputedStyle(el);
    Object.assign(el.style, { left: now.left, right: now.right });
    const movingRight = index === 1;
    const settle = () => { if (shown.current === index) Object.assign(el.style, insets(index)); };
    const lead = animate(el, movingRight ? { right: target.right } : { left: target.left }, EDGE_LEAD);
    const trail = animate(el, movingRight ? { left: target.left } : { right: target.right }, EDGE_TRAIL);
    void Promise.all([lead, trail]).then(settle);
    // insets is derived from pad and gap, which are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate, index, reduced, scope, pad, gap]);
  return <span ref={scope} className={className} style={restInsets} aria-hidden="true" />;
}
